import { specialCartStore } from '@/api/cart';
import { IPointsMallInfo } from '@/api/types/account';
import { IProductDetail } from '@/api/types/public';
import { useJump } from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { YES_NO } from '@/utils/enums/common';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React, { useContext, useState } from 'react';

interface FixedBottomButtonPointsMallProps {
  detail: IProductDetail;
  pointsMallData?: IPointsMallInfo;
  refreshFreeData?: () => void;
  containerStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  handledData?: {
    productDetail: any;
    [key: string]: any;
  };
}

const FixedBottomButtonPointsMall: React.FC<FixedBottomButtonPointsMallProps> = ({
  detail,
  pointsMallData,
  containerStyle,
  buttonStyle,
  refreshFreeData,
  handledData,
}) => {
  const t = useTranslations(
    'account.redeemableItems.RedeemedDetailDialog.fixedBottomButtonPointsMall',
  );
  const router = useRouter();
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [selectLensesLoading, setSelectLensesLoading] = useState<boolean>(false);
  const setCannotSubmitData = useStore((state) => state.setCannotSubmitData);
  const [joinMemberLoading, setJoinMemberLoading] = useState(false);
  const currency = useStore((state) => state.currency);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const token = useStore((state) => state.token);
  const clientUserInfo = useStore((state) => state.userInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setJoinFreeDialogInfo = useStore((state) => state.setJoinFreeDialogInfo);
  const { deviceType } = useContext(ResponsiveContext);
  const toJumpTarget = useJump();
  const { trackEvent } = useReporting();
  const FixedButtonContainer = styled(Box)(() => ({
    position: deviceType === EPlatform.WEB ? 'absolute' : 'fixed',
    bottom: deviceType === EPlatform.WEB ? '-16px' : '0',
    right: deviceType === EPlatform.WEB ? 'auto' : '0',
    width: deviceType === EPlatform.WEB ? 'calc(100% - 478px)' : '100%',
    left: deviceType === EPlatform.WEB ? '432px' : 0,
    backgroundColor: '#ffffff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    flexDirection: deviceType === EPlatform.WEB ? 'row' : 'row-reverse',
    paddingBottom: deviceType === EPlatform.WEB ? '16px' : '0',
    paddingTop: deviceType === EPlatform.WEB ? '16px' : '0',
    ...containerStyle,
  }));

  const SelectButton = styled(Button)(() => ({
    borderRadius: '4px',
    height: '48px',
    fontSize: '14px',
    textTransform: 'none',
    fontFamily: font.bb,
    flex: 1,
    display: 'block',
    boxShadow: 'none',
    ...buttonStyle,
  }));

  const signIn = () => {
    PointsMallProductDetailsPageBtn_Click('Join for Free');
    if (token) {
      handleJoinMember();
    } else {
      setShowLoginRegisterDialog(true);
    }
  };

  const handleJoinMember = async () => {
    setJoinMemberLoading(true);
    setJoinFreeDialogInfo({
      show: true,
    });
    setJoinMemberLoading(false);
    setTimeout(() => {
      refreshAccountInfo();
    }, 3000);
  };

  // const handlePrescriptionTypeAll = async () => {
  //   const res = await getPrescriptionTypeAll();
  //   // 过滤 select lens 第一步的选择处方类型 传递的值
  //   if (res.code === 200) {
  //     const glassPrescriptionType = detail.prescription_type as any[];
  //     const glassPresTypeList = [];
  //     const presTypeAll = res.data;
  //     if (glassPrescriptionType && Array.isArray(glassPrescriptionType)) {
  //       glassPrescriptionType.forEach((elm) => {
  //         const found = presTypeAll.find((el) => el.title === elm);
  //         if (found) glassPresTypeList.push(found);
  //       });
  //     }
  //     return glassPresTypeList;
  //   }
  //   return [];
  // };

  const handleSelectLenses = async () => {
    try {
      PointsMallProductDetailsPageBtn_Click('Select Lenses');
      setSelectLensesLoading(true);
      // const glassPresTypeList = await handlePrescriptionTypeAll();

      const { productDetail } = handledData || {};
      sessionStorage.setItem('zl-sensors-lens-entrance-scene', `积分商城|Select Lenses`);
      router.push(`/lens-process?sku=${productDetail.shop_sku}&is-redeem=1`);
    } catch (error) {
      logger.error(error);
    } finally {
      setSelectLensesLoading(false);
    }
  };

  const handleRedeem = async () => {
    PointsMallProductDetailsPageBtn_Click('Redeem');
    if (!token) {
      setShowLoginRegisterDialog(true);
      return;
    }
    try {
      setBtnLoading(true);
      const params = {
        currency: currency.name,
        data: [
          {
            type: 6,
            relation_id: 0,
            products: [
              {
                product_id: detail.id || 0,
                conversion_qty: 1,
              },
            ],
          },
        ],
      };
      const res: any = await specialCartStore(params);
      if (res?.code === 200) {
        sessionStorage.setItem('zl-sensors-entrance-payment-scene', '积分商城详情页兑换');
        toJumpTarget({
          jump_type: 3,
          Jump_target: `/payment?redeemCartId=${res?.data?.cart_ids?.[0]}`,
        });
      } else if (res?.code === 400880) {
        // 打开不可结算弹窗
        setCannotSubmitData({
          ...res.data,
          show: true,
        });
      } else if (res.code === 400600) {
        // 获取 用户信息
        toast.error(res.msg);
        await refreshAccountInfo();
        if (refreshFreeData) {
          refreshFreeData();
        }
      } else {
        toast.error(res.msg);
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const PointsMallProductDetailsPageBtn_Click = (btnName: string) => {
    trackEvent(
      {
        eventName: 'PointsMallProductDetailsPageBtn_Click',
        properties: {
          product_class_name: detail?.products_category,
          is_product_lens: detail?.is_glass === 1 && detail?.product_lens === 1,
          shop_sku: detail?.shop_sku,
          product_sku: detail?.sku,
          business_sku: detail?.business_sku,
          product_id: `${detail?.id || ''}`,
          product_name: detail?.name,
          btn_name: btnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  // 渲染按钮组
  const renderButtons = () => {
    const showFrameOnly = detail.is_frame_only === 1; // 是否展示 frame only 按钮 1：展示 2：不展示
    const isGlass = detail.is_glass === 1; // 1是眼镜 2不是眼镜
    const isLens = detail.product_lens === 1; // 是否支持配镜 1：支持 2：不支持
    const showAddToCart = detail?.is_show_add_to_cart === 1; // 是否展示 Add To Cart 按钮 1：展示 2：不展示
    const showSelectLens = detail?.is_show_add_lense === 1; // 是否展示 Select Lenses 按钮 1：展示 2：不展示
    const showSoldOutBtn = (detail?.qty || 0) <= 0; // 商品数量是否为售罄
    const showDiscountBtn = detail?.select_lens_config?.switch === YES_NO.YES; // 是否展示折扣按钮 1：展示 2：不展示
    const discountBtnText = detail?.select_lens_config?.text; // 折扣按钮文案

    // 通用的 Redeem 按钮
    const RedeemButton = ({
      sx = {},
      variant = 'contained',
      color = 'primary',
    }: {
      sx?: React.CSSProperties;
      variant?: 'contained' | 'outlined';
      color?: 'primary' | 'inherit';
    }) => (
      <SelectButton
        variant={variant}
        color={color}
        onClick={handleRedeem}
        loading={btnLoading}
        disabled={btnLoading}
        sx={sx}
      >
        <Box>{t('redeem')}</Box>
      </SelectButton>
    );

    // 1. 未入会判断
    if (clientUserInfo.is_member !== 1 || !token) {
      return (
        <SelectButton
          color="primary"
          loading={joinMemberLoading}
          onClick={signIn}
          variant="contained"
        >
          {t('joinForFree')}
        </SelectButton>
      );
    }

    // 2. 商品售罄判断
    if (showSoldOutBtn) {
      return (
        <SelectButton color="primary" disabled variant="contained">
          <Box sx={{ color: '#ffffff' }}>{t('soldOut')}</Box>
        </SelectButton>
      );
    }

    // 3. 积分不足判断
    if ((pointsMallData?.points || 0) < (detail?.point || 0)) {
      return (
        <SelectButton color="primary" disabled variant="contained">
          <Box sx={{ color: '#ffffff' }}>{t('insufficientPoints')}</Box>
        </SelectButton>
      );
    }

    // 4. 非眼镜商品或不支持配镜的眼镜
    if (!isGlass || (isGlass && !isLens)) {
      return <RedeemButton />;
    }

    // 5. 眼镜商品的特殊处理
    if (isGlass && isLens) {
      return (
        <>
          {showSelectLens && (
            <SelectButton
              color="primary"
              variant="contained"
              onClick={handleSelectLenses}
              style={{ position: 'relative' }}
              disabled={selectLensesLoading}
              loading={selectLensesLoading}
              sx={{ flex: showFrameOnly || showAddToCart ? '60%' : '100%' }}
            >
              {showDiscountBtn && (
                <Box className="absolute top-[-12px] right-[8px] overflow-hidden text-ellipsis whitespace-nowrap px-[10px] max-w-full h-[24px] leading-[24px] box-border text-[#282828] text-xs rounded-xl bg-[#FCA6FF]">
                  {discountBtnText}
                </Box>
              )}
              <Box sx={{ color: '#ffffff' }}>{t('selectLenses')}</Box>
            </SelectButton>
          )}
          {(showFrameOnly || showAddToCart) && (
            <RedeemButton
              sx={{
                flex: showSelectLens ? '36%' : '100%',
                borderColor: showSelectLens ? colors.auxiliaryDarkGray : colors.primaryPurple,
              }}
              variant={showSelectLens ? 'outlined' : 'contained'}
              color={showSelectLens ? 'inherit' : 'primary'}
            />
          )}
        </>
      );
    }
    return null; // 处理所有其他情况
  };

  return <FixedButtonContainer>{renderButtons()}</FixedButtonContainer>;
};

export default FixedBottomButtonPointsMall;
